// Belgian postcodes are handed out per province in fixed ranges (bpost), so the province follows
// from the postcode alone. The Flemish address register does not return it.
const BELGIAN_PROVINCES_BY_POSTCODE = [
  [1000, 1299, 'Brussels Hoofdstedelijk Gewest'],
  [1300, 1499, 'Waals-Brabant'],
  [1500, 1999, 'Vlaams-Brabant'],
  [2000, 2999, 'Antwerpen'],
  [3000, 3499, 'Vlaams-Brabant'],
  [3500, 3999, 'Limburg'],
  [4000, 4999, 'Luik'],
  [5000, 5999, 'Namen'],
  [6000, 6599, 'Henegouwen'],
  [6600, 6999, 'Luxemburg'],
  [7000, 7999, 'Henegouwen'],
  [8000, 8999, 'West-Vlaanderen'],
  [9000, 9999, 'Oost-Vlaanderen']
]

// Dutch and Spanish particles, e.g. 'Sint-Gillis-bij-Dendermonde', 'Calle de la Paz'.
const LOWERCASE_PLACE_NAME_PARTICLES = ['aan', 'bij', 'de', 'den', 'het', 'in', 'op', 'over', 'ten', 'ter', 'van', 'del', 'el', 'la', 'las', 'los', 'y']

// 1-3 digits with an optional letter suffix; four digits would be a Belgian or Dutch postcode.
const HOUSE_NUMBER_PATTERN = /^\d{1,3}[a-z]{0,2}$/i

export const ADDRESS_SEARCH_MARK_KINDS = {
  AUTO: 'auto',
  NOT_IN_REGISTER: 'notInRegister',
  EDITED: 'edited'
}

export default {
  sameValue (a, b) {
    return String(a === undefined || a === null ? '' : a) === String(b === undefined || b === null ? '' : b)
  },

  /**
   * A mark ({ kind, source, value }) remembers the value the address search filled in, so a field
   * that no longer holds that value has been changed by hand since.
   */
  getMarkKind (mark, currentValue) {
    return this.sameValue(currentValue, mark.value) ? mark.kind : ADDRESS_SEARCH_MARK_KINDS.EDITED
  },

  getBelgianProvince (postcode) {
    const number = parseInt(postcode, 10)
    const range = BELGIAN_PROVINCES_BY_POSTCODE.find(([from, to]) => number >= from && number <= to)
    return range ? range[2] : ''
  },

  /**
   * Turns an upper-case register name into the usual spelling, e.g. 'SINT-GILLIS-BIJ-DENDERMONDE'
   * into 'Sint-Gillis-bij-Dendermonde'.
   */
  toPlaceNameCase (name) {
    let wordIndex = 0
    return String(name || '').trim().toLowerCase().replace(/[^\s-]+/g, (word) => {
      const isParticle = wordIndex > 0 && LOWERCASE_PLACE_NAME_PARTICLES.includes(word)
      wordIndex++
      return isParticle ? word : word.charAt(0).toUpperCase() + word.slice(1)
    })
  },

  /**
   * The deelgemeente of a cadastral department, e.g. 'DENDERMONDE 4 AFD/GREMBERGEN/' gives
   * 'Grembergen'. Departments of a municipality without deelgemeenten ('GENT 1 AFD') give ''.
   */
  getPlaceFromCadastralDepartment (departmentName) {
    const place = String(departmentName || '').split('/')[1]
    return place && place.trim() ? this.toPlaceNameCase(place) : ''
  },

  getHouseNumber (query) {
    const token = String(query || '').split(/[\s,]+/).find((part) => HOUSE_NUMBER_PATTERN.test(part))
    return token ? token.toUpperCase() : ''
  },

  removeHouseNumber (query) {
    return String(query || '').split(/\s+/).filter((part) => !HOUSE_NUMBER_PATTERN.test(part.replace(/,$/, ''))).join(' ')
  },

  /**
   * Whether the street part of a suggestion label ('Zeelsebaan 25, 9200 Dendermonde') carries this
   * house number.
   */
  labelHasHouseNumber (label, houseNumber) {
    const streetPart = String(label || '').split(',')[0]
    return streetPart.split(/\s+/).some((part) => part.toUpperCase() === String(houseNumber).toUpperCase())
  },

  formatDutchPostcode (postcode) {
    const match = /^(\d{4})\s*([a-z]{2})$/i.exec(String(postcode || '').trim())
    return match ? `${match[1]} ${match[2].toUpperCase()}` : String(postcode || '')
  },

  /** CartoCiudad writes the street in capitals: 'CALLE MAYOR 10, Madrid' gives 'Calle Mayor 10, Madrid'. */
  formatCartoCiudadAddress (address) {
    const [streetPart, ...rest] = String(address || '').split(',')
    return [this.toPlaceNameCase(streetPart), ...rest].join(',')
  },

  /** The street of a CartoCiudad address, without the portal number and town: 'Calle Mayor'. */
  getCartoCiudadStreet (address, portalNumber) {
    const streetPart = String(address || '').split(',')[0]
    const withoutNumber = portalNumber ? streetPart.replace(new RegExp(`\\s+${portalNumber}(\\s.*)?$`), '') : streetPart
    return this.toPlaceNameCase(withoutNumber)
  },

  /** A suggestion label for an OpenStreetMap (Photon) result: 'Unter den Linden 77, 10117 Berlin, Deutschland'. */
  getPhotonLabel (properties) {
    const first = properties.type === 'house'
      ? [properties.street || properties.name, properties.housenumber].filter(Boolean).join(' ')
      : properties.name
    const town = [properties.postcode, properties.city].filter(Boolean).join(' ')
    return [first, town !== first ? town : '', properties.country].filter(Boolean).join(', ')
  },

  /**
   * The place of an OpenStreetMap (Photon) result. A hamlet or quarter (district, locality) lies
   * within its city: the buurtschap Langstraat gives Achthuizen.
   */
  getPhotonPlace (properties) {
    if (properties.type === 'city') return properties.name || ''
    if (properties.type === 'district' || properties.type === 'locality') return properties.city || properties.name || ''
    return properties.city || ''
  },

  /**
   * The level that goes into Provincie: in OpenStreetMap an Italian provincia is the county (the
   * state is the regione); elsewhere the state is the province, Bundesland or województwo.
   */
  getPhotonProvince (properties) {
    if (properties.countrycode === 'IT') return properties.county || properties.state || ''
    return properties.state || properties.county || ''
  },

  /**
   * An address as one search text ('Zeelsebaan 25, 9200 Grembergen'), or '' without a postcode or
   * place: a street name alone is too ambiguous to put a map on.
   */
  formatAddressQuery ({ street, houseNumber, postcode, place, municipality }) {
    const town = [postcode, place || municipality].filter(Boolean).join(' ')
    if (!town) return ''
    return [[street, houseNumber].filter(Boolean).join(' '), town].filter(Boolean).join(', ')
  }
}
